import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LanguageSelector } from './language-selector';
import { BrandLogo } from '@/components/branding/brand-logo';
import { useEffectiveBranding } from '@/hooks/use-effective-branding';
import { useBrandTheme } from '@/hooks/use-brand-theme';
import { useAcquisitionTracking } from '@/hooks/use-acquisition-tracking';
import { useSafeRedirectUrl } from '@/hooks/use-safe-redirect-url';
import { saveOnboardingIntent, getOnboardingState } from '@/lib/onboarding';
import { PRIMARY_APP_ORIGIN, safePartnerRedirectUrl } from '@/config/clerk-satellite';
import { config } from '@/lib/config';

type PartnerOtpMode = 'sign-in' | 'sign-up';

/**
 * Partner-host OTP sign-in AND implicit sign-up.
 *
 * Runs ONLY on partner whitelabel satellite hosts (e.g.
 * `app.tagadapartnerdemo.app`). The entire email + OTP entry happens
 * here, on the partner domain — the user never types credentials on the
 * primary, and there is NO Google button (the Google consent screen
 * would leak the "Tagada" OAuth app name, breaking the whitelabel).
 *
 * Why not the regular Clerk flow (CustomSignIn / CustomSignUp)? Clerk
 * rejects sign-in/sign-up CREATION on satellites
 * (`operation_not_allowed_on_satellite_domain`). So instead of
 * `signIn.create` / `signUp.create` we drive a self-owned OTP against
 * our backend, which looks up OR creates the Clerk user (implicit
 * sign-up) and returns a single-use Clerk sign-in token. We hand that
 * ticket to the primary's `/accept-sign-in` page, which consumes it
 * (allowed on the primary) and bounces back here with an active session
 * via Clerk's cross-domain handshake.
 *
 * Sign-in vs sign-up is purely cosmetic here (copy + footer link): the
 * backend's lookup-or-create makes both the same OTP exchange. A brand
 * new user lands signed-in with no org; the onboarding intent we plant
 * below routes them to `/sign-up-new?mode=sso`, the satellite-safe
 * org-creation wizard (server-side `completeHubSignup`, no Clerk client
 * creation calls).
 */
export function PartnerOtpSignIn({ mode = 'sign-in' }: { mode?: PartnerOtpMode }) {
  const { t } = useTranslation('auth');
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();
  const { search } = useLocation();
  const allowedRedirectUrl = useSafeRedirectUrl();

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const brand = useEffectiveBranding();
  useBrandTheme(brand);
  useAcquisitionTracking();

  // Plant an onboarding intent so a brand-new user (auto-created via OTP,
  // no org yet) is routed to /sign-up-new?mode=sso after landing — the
  // satellite-safe org-creation path — instead of /no-organization which
  // relies on client-side Clerk org creation. Mirrors CustomSignUp.
  useEffect(() => {
    if (getOnboardingState()) return;
    saveOnboardingIntent({ type: 'store_create', source: 'partner_otp' });
  }, []);

  const partnerAppHost = typeof window !== 'undefined' ? window.location.hostname : '';
  const redirectUrl = safePartnerRedirectUrl(allowedRedirectUrl, partnerAppHost);

  // Already signed in (revisit / second tab) → go to the requested
  // partner page, else home; the satellite SDK already holds the session
  // cookie.
  useEffect(() => {
    if (!isSignedIn) return;
    if (redirectUrl) {
      window.location.replace(redirectUrl);
    } else {
      navigate('/');
    }
  }, [isSignedIn, redirectUrl, navigate]);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${config.apiBaseUrl}/api/public/crm/partner-otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), partnerAppHost }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data?.reason === 'cooldown' || res.status === 429) {
          setError(
            t('signIn.errors.tooSoon'),
          );
        } else {
          setError(t('signIn.errors.sendCodeFailed'));
        }
        return;
      }
      setStep('code');
    } catch {
      setError(t('signIn.errors.sendCodeFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${config.apiBaseUrl}/api/public/crm/partner-otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), code: code.trim(), partnerAppHost }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ticket) {
        if (data?.reason === 'expired' || data?.reason === 'too_many_attempts') {
          setError(
            t('signIn.errors.codeExpired'),
          );
        } else {
          setError(t('signIn.errors.invalidCode'));
        }
        return;
      }

      // Hand the ticket to the primary's /accept-sign-in bridge, asking
      // it to return here (or to the validated partner redirect_url) once
      // the session is active. This is the only moment the primary host
      // is involved — an invisible spinner flash.
      const acceptUrl = new URL(`${PRIMARY_APP_ORIGIN}/accept-sign-in`);
      acceptUrl.searchParams.set('__clerk_ticket', data.ticket);
      acceptUrl.searchParams.set('redirect_url', redirectUrl ?? `${window.location.origin}/`);
      window.location.replace(acceptUrl.toString());
    } catch {
      setError(t('signIn.errors.invalidCode'));
      setLoading(false);
    }
  };

  const titleKey = mode === 'sign-up' ? 'signUp.title' : 'signIn.title';
  const subtitleKey = mode === 'sign-up' ? 'signUp.subtitle' : 'signIn.subtitle';

  return (
    <div className="flex min-h-screen items-center justify-center p-8">
      <div className="w-full max-w-md space-y-8">
        <div className="flex justify-end">
          <LanguageSelector />
        </div>

        <div className="flex justify-center">
          <BrandLogo brand={brand} />
        </div>

        {step === 'email' ? (
          <>
            <div className="space-y-2 text-center">
              <h1 className="text-foreground text-3xl font-bold tracking-tight">{t(titleKey)}</h1>
              <p className="text-muted-foreground">{t(subtitleKey)}</p>
            </div>

            <form onSubmit={handleSendCode} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('signIn.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  autoCapitalize="none"
                  autoComplete="email"
                  autoCorrect="off"
                  placeholder={t('signIn.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>

              <div className="bg-muted/50 rounded-lg border p-4">
                <p className="text-muted-foreground text-sm">{t('signIn.otpInfo')}</p>
              </div>

              {error && (
                <div className="bg-destructive/10 rounded-md p-3">
                  <p className="text-destructive text-sm">{error}</p>
                </div>
              )}

              <Button className="w-full" type="submit" disabled={loading}>
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="border-primary-foreground h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                    {t('signIn.sendingCode')}
                  </div>
                ) : (
                  t('signIn.sendCode')
                )}
              </Button>
            </form>

            {mode === 'sign-up' ? (
              <p className="text-muted-foreground text-center text-sm">
                {t('signUp.haveAccount')}{' '}
                <Link
                  to={{ pathname: '/sign-in', search }}
                  className="text-foreground hover:text-primary font-semibold underline underline-offset-4"
                >
                  {t('signUp.signInLink')}
                </Link>
              </p>
            ) : (
              <p className="text-muted-foreground text-center text-sm">
                {t('signIn.noAccount')}{' '}
                <Link
                  to={{ pathname: '/sign-up', search }}
                  className="text-foreground hover:text-primary font-semibold underline underline-offset-4"
                >
                  {t('signIn.signUpLink')}
                </Link>
              </p>
            )}
          </>
        ) : (
          <>
            <div className="space-y-2 text-center">
              <h1 className="text-foreground text-3xl font-bold tracking-tight">
                {t('signIn.otpVerification.title')}
              </h1>
              <p className="text-muted-foreground">
                {t('signIn.otpVerification.subtitle')} <strong>{email}</strong>
              </p>
            </div>

            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">{t('signIn.otpVerification.codeLabel')}</Label>
                <Input
                  id="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder={t('signIn.otpVerification.codePlaceholder')}
                  className="text-center text-2xl tracking-widest"
                  maxLength={6}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  disabled={loading}
                  required
                />
                <p className="text-muted-foreground text-xs">{t('signIn.otpVerification.codeHelper')}</p>
              </div>

              {error && (
                <div className="bg-destructive/10 rounded-md p-3">
                  <p className="text-destructive text-sm">{error}</p>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="border-primary-foreground h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                    {t('signIn.otpVerification.verifying')}
                  </div>
                ) : (
                  t('signIn.otpVerification.verifyButton')
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setStep('email');
                  setCode('');
                  setError('');
                }}
                disabled={loading}
              >
                {t('signIn.otpVerification.backButton')}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
